import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_API_URL}/api`;

// Runtime check for API URL
if (!process.env.REACT_APP_API_URL) {
  console.error('[AuthContext] CRITICAL: REACT_APP_API_URL is not defined! API calls will fail.');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("speakr_token"));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      // Log the actual URL being called for debugging
      console.log('[AuthContext] Fetching user from:', `${API}/auth/me`);
      
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
      setAuthError(null);
    } catch (error) {
      console.error("[AuthContext] Auth error:", error?.response?.status, error?.message);
      // Surface the error for debugging
      if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
        setAuthError('Network error - check if REACT_APP_API_URL is correct');
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, business } = res.data;
    localStorage.setItem("speakr_token", newToken);
    setToken(newToken);
    setUser(business);
    return business;
  };

  const register = async (name, industry, email, password, phone_number) => {
    const res = await axios.post(`${API}/auth/register`, {
      name,
      industry,
      email,
      password,
      phone_number
    });
    const { token: newToken, business } = res.data;
    localStorage.setItem("speakr_token", newToken);
    setToken(newToken);
    setUser(business);
    return business;
  };

  const logout = () => {
    localStorage.removeItem("speakr_token");
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{
      user,
      business: user, // Alias for backward compatibility with components using business
      token,
      loading,
      authError,
      login,
      register,
      logout,
      getAuthHeader,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
